import type { SearchAppData } from '@global-types/searchAppData.ts';
import { defineStore, storeToRefs } from 'pinia';
import { ref } from 'vue';
import { useScreensizeStore } from '@stores/useScreensizeStore.ts';
import { useLoading } from '@composables/useLoading.ts';
import type { AlgoliaOffer } from '@global-types/algoliaOffer';
import { getAlgoliaOffers, getAlgoliaSpacesCount } from '@api/algolia/apiAlgolia';

export const useSearchStore = defineStore('search', () => {
  const searchAppData = ref<SearchAppData>({ favorite_offers: [] });

  const {
    isLoading,
    startLoading,
    stopLoading,
  } = useLoading();

  const offers = ref<AlgoliaOffer[]>([]);
  const nbOffers = ref<number>(0);
  const nbSpaces = ref<number>(0);

  const initSearchData = (): void => {
    const dataElement = document.getElementById('search-data');

    if (!dataElement) {
      return;
    }

    searchAppData.value = JSON.parse(dataElement.textContent) as SearchAppData;
  };

  initSearchData();

  const isFavorite = (hashId: string): boolean => searchAppData.value.favorite_offers.includes(hashId);

  const addFavorite = (hashId: string): void => {
    if (!isFavorite(hashId)) {
      searchAppData.value.favorite_offers.push(hashId);
    }
  };

  const removeFavorite = (hashId: string): void => {
    searchAppData.value.favorite_offers = searchAppData.value.favorite_offers.filter((id) => id !== hashId);
  };

  const fetchOffers = async (): Promise<void> => {
    startLoading();

    const data = await getAlgoliaOffers();

    offers.value = data?.hits ?? [];
    nbOffers.value = data?.nbHits ?? 0;

    stopLoading();
  };

  fetchOffers();

  const fetchSpacesCount = async (): Promise<void> => {
    startLoading();

    const count = await getAlgoliaSpacesCount();

    nbSpaces.value = count ?? 0;

    stopLoading();
  };

  fetchSpacesCount();

  const { isMobileOrTablet } = storeToRefs(useScreensizeStore());

  const mapIsVisible = ref<boolean>(!isMobileOrTablet.value);

  const openMap = (): void => {
    mapIsVisible.value = !mapIsVisible.value;
  };

  const closeMap = (): void => {
    mapIsVisible.value = false;
  };

  return {
    isLoading,
    offers,
    nbOffers,
    nbSpaces,
    isFavorite,
    addFavorite,
    removeFavorite,
    mapIsVisible,
    openMap,
    closeMap,
  };
});
